import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import api from '../utils/api';
import './BookSessionModal.css';

// Quick session-booking modal: name + mobile + email only. POSTs to the
// existing /api/enquiries.php — when consultantId is set the enquiry also
// notifies that practitioner directly (enquiries.php routing), otherwise it
// goes to the global admin recipients with source 'home-team'.
// Reusable: pass expertName (display only), optional consultantId, onClose.
function BookSessionModal({ expertName, consultantId, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Lock body scroll while open; close on Escape.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone.trim())) return 'Please enter a valid mobile number.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Please enter a valid email address.';
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setBusy(true);
    try {
      await api.submitEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        topic: `Session with ${expertName}`,
        message: `Session booking request from the homepage team section (expert: ${expertName}).`,
        consultant_id: consultantId || undefined,
        source: 'home-team',
      });
      setDone(true);
    } catch (err) {
      setError(err && err.message ? err.message : 'Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="book-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Book a session with ${expertName}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="book-modal__card">
        <button type="button" className="book-modal__close" aria-label="Close" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        {done ? (
          <div className="book-modal__done">
            <div className="book-modal__done-icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3>Request received</h3>
            <p>Thanks — we&rsquo;ll reach out shortly to schedule your session with {expertName}.</p>
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <h3 className="book-modal__title">Book a session</h3>
            <p className="book-modal__sub">with {expertName}</p>
            <form onSubmit={submit} noValidate>
              <label className="book-modal__field">
                <span>Your name</span>
                <input type="text" value={form.name} onChange={set('name')} autoComplete="name" required />
              </label>
              <label className="book-modal__field">
                <span>Mobile</span>
                <input type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" required />
              </label>
              <label className="book-modal__field">
                <span>Email</span>
                <input type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
              </label>
              {error && <p className="book-modal__error" role="alert">{error}</p>}
              <button type="submit" className="btn-primary book-modal__submit" disabled={busy}>
                {busy ? 'Sending…' : 'Request Session'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default BookSessionModal;
